import React, { useState, useEffect } from 'react';
import { View, FlatList, StyleSheet, TouchableOpacity, RefreshControl } from 'react-native';
import { Text, Card, Avatar, FAB, Searchbar, useTheme, ActivityIndicator, Chip, Button, Dialog, Portal, Paragraph, Snackbar } from 'react-native-paper';
import { getAllCategories, deleteCategory } from '../../../api/adminAPI';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const CategoryListScreen = ({ navigation }) => {
  const theme = useTheme();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await getAllCategories();
      if (response.success) {
        setCategories(response.data || []);
        setFilteredCategories(response.data || []);
      } else {
        setSnackbarMessage('Kategoriler alınırken bir hata oluştu');
        setSnackbarVisible(true);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setSnackbarMessage('Kategoriler alınırken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // ilk yükleme
    fetchCategories();
    
    // Ekrana her dönüldüğünde yeniden yükle
    const unsubscribe = navigation.addListener('focus', () => {
      fetchCategories();
    });
    
    return unsubscribe;
  }, [navigation]);

  // Arama yapma
  useEffect(() => {
    if (searchQuery) {
      const filtered = categories.filter(
        category => category.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCategories(filtered);
    } else {
      setFilteredCategories(categories);
    }
  }, [searchQuery, categories]);

  const onRefresh = React.useCallback(() => {
    setRefreshing(true);
    fetchCategories();
  }, []);

  const confirmDelete = (category) => {
    setCategoryToDelete(category);
    setDeleteDialogVisible(true);
  };

  const handleDeleteCategory = async () => {
    try {
      if (!categoryToDelete) return;
      
      const response = await deleteCategory(categoryToDelete.id);
      
      if (response.success) {
        // Kategoriyi listeden kaldır
        setCategories(prevCategories => 
          prevCategories.filter(cat => cat.id !== categoryToDelete.id)
        );
        setSnackbarMessage('Kategori başarıyla silindi');
      } else {
        setSnackbarMessage('Kategori silinirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      setSnackbarMessage('Kategori silinirken bir hata oluştu');
    } finally {
      setDeleteDialogVisible(false);
      setCategoryToDelete(null);
      setSnackbarVisible(true);
    }
  };

  const navigateToEditCategory = (category) => {
    navigation.navigate('CategoryForm', { category });
  };

  const navigateToCategoryItems = (category) => {
    navigation.navigate('MenuItems', { 
      categoryId: category.id,
      categoryName: category.name
    });
  };

  const renderCategoryItem = ({ item }) => (
    <Card style={styles.card}>
      <Card.Content style={styles.cardContent}>
        <View style={styles.cardLeft}>
          {item.image_url ? (
            <Avatar.Image size={60} source={{ uri: item.image_url }} />
          ) : (
            <Avatar.Icon size={60} icon="food-variant" />
          )}
          
          <View style={styles.cardTextContainer}>
            <Text style={styles.categoryName}>{item.name}</Text>
            {item.description && (
              <Text style={styles.categoryDescription} numberOfLines={2}>
                {item.description}
              </Text>
            )}
            <Chip 
              icon={item.is_active ? "check-circle" : "close-circle"} 
              style={[styles.statusChip, {backgroundColor: item.is_active ? '#E8F5E9' : '#FFEBEE'}]}
            >
              {item.is_active ? 'Aktif' : 'Pasif'}
            </Chip>
          </View>
        </View>
        
        <View style={styles.cardActions}>
          <TouchableOpacity 
            style={styles.iconButton}
            onPress={() => navigateToCategoryItems(item)}
          >
            <MaterialCommunityIcons name="silverware-fork-knife" size={24} color={theme.colors.primary} />
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.iconButton}
            onPress={() => navigateToEditCategory(item)}
          >
            <MaterialCommunityIcons name="pencil" size={24} color={theme.colors.primary} />
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.iconButton}
            onPress={() => confirmDelete(item)}
          >
            <MaterialCommunityIcons name="delete" size={24} color="red" />
          </TouchableOpacity>
        </View>
      </Card.Content>
    </Card>
  );

  return (
    <View style={styles.container}>
      <Searchbar
        placeholder="Kategori Ara..."
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={styles.searchbar}
      />

      {loading && !refreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>Kategoriler yükleniyor...</Text>
        </View>
      ) : filteredCategories.length === 0 ? (
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons name="food-off" size={80} color="#BDBDBD" />
          <Text style={styles.emptyText}>Henüz kategori eklenmemiş</Text>
          <Button 
            mode="contained" 
            onPress={() => navigation.navigate('CategoryForm')}
            style={styles.addButton}
          >
            Yeni Kategori Ekle
          </Button>
        </View>
      ) : (
        <FlatList
          data={filteredCategories}
          keyExtractor={(item) => item.id.toString()}
          renderItem={renderCategoryItem}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        />
      )}

      <FAB
        style={[styles.fab, { backgroundColor: theme.colors.primary }]}
        icon="plus"
        onPress={() => navigation.navigate('CategoryForm')}
      />

      <Portal>
        <Dialog visible={deleteDialogVisible} onDismiss={() => setDeleteDialogVisible(false)}>
          <Dialog.Title>Kategori Silme</Dialog.Title>
          <Dialog.Content>
            <Paragraph>
              "{categoryToDelete?.name}" kategorisini silmek istediğinizden emin misiniz?
              Bu işlem geri alınamaz. Bu kategorideki tüm ürünler de silinecektir.
            </Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeleteDialogVisible(false)}>İptal</Button>
            <Button onPress={handleDeleteCategory} color="red">Sil</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
      >
        {snackbarMessage}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  searchbar: {
    marginHorizontal: 16,
    marginVertical: 8,
    elevation: 2,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
  },
  listContent: {
    padding: 8,
  },
  card: {
    marginVertical: 6,
    marginHorizontal: 8,
    elevation: 2,
  },
  cardContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  cardTextContainer: {
    marginLeft: 16,
    flex: 1,
  },
  categoryName: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  categoryDescription: {
    marginTop: 4,
    fontSize: 14,
    color: '#757575',
  },
  cardActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconButton: {
    padding: 8,
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  emptyText: {
    fontSize: 18,
    marginTop: 16,
    marginBottom: 24,
    color: '#757575',
  },
  addButton: {
    paddingHorizontal: 8,
  },
  statusChip: {
    marginTop: 6,
    height: 26,
    alignSelf: 'flex-start',
  },
});

export default CategoryListScreen;
